// Types match the backend Pydantic schemas.

export type StudentRole = "medic" | "rezident" | "asistent";
export type AdminRole = "super_admin" | "editor" | "examinator" | "support";
export type Role = StudentRole | AdminRole;

export const STUDENT_ROLES: Role[] = ["medic", "rezident", "asistent"];
export const ADMIN_ROLES: Role[] = ["super_admin", "editor", "examinator", "support"];

export const ROLE_LABELS: Record<Role, string> = {
  super_admin: "Super administrator",
  editor: "Editor curs",
  examinator: "Examinator",
  support: "Suport",
  medic: "Medic",
  rezident: "Rezident",
  asistent: "Asistent",
};

export interface User {
  id: string;
  email: string;
  full_name: string;
  role: Role;
  specialty: string | null;
  is_active: boolean;
  created_at: string;
}

export interface InstructorBrief {
  id: string;
  full_name: string;
}

export type CourseStatus = "draft" | "published" | "archived";
export type CourseLevel = "beginner" | "intermediate" | "advanced";
export type LessonType = "text" | "video" | "mixed";
export type MediaStatus = "pending" | "uploaded" | "transcoding" | "ready" | "failed";

export interface MediaAsset {
  id: string;
  status: MediaStatus;
  duration_seconds: number | null;
  content_type: string | null;
}

export interface Lesson {
  id: string;
  course_id: string;
  title: string;
  position: number;
  type: LessonType;
  duration_minutes: number | null;
  text_content: string | null;
  video_asset: MediaAsset | null;
  is_required: boolean;
}

export interface CourseListItem {
  id: string;
  title: string;
  slug: string;
  subtitle: string | null;
  category: string;
  level: CourseLevel;
  duration_minutes: number | null;
  cover_image_url: string | null;
  status: CourseStatus;
  audiences: string[];
  instructor: InstructorBrief | null;
}

export interface CourseDetail extends CourseListItem {
  description: string | null;
  language: string;
  emc_credits: number | null;
  certificate_validity_months: number | null;
  lessons: Lesson[];
  created_at: string;
  updated_at: string;
}

export interface CourseProgress {
  course_id: string;
  total_lessons: number;
  completed_lessons: number;
  percent: number;
}

export interface StudentDashboard {
  active_courses: number;
  lessons_completed: number;
  exam_average: number | null;
  certificates: number;
}

export interface AdminStats {
  students_total: number;
  students_active: number;
  courses_published: number;
  courses_draft: number;
  exam_attempts_total: number;
  pass_rate: number;
  students_by_role: Record<string, number>;
}

export interface ExamAnswer {
  id: string;
  position: number;
  text: string;
  is_correct?: boolean;
}

export type QuestionType = "single" | "multiple" | "true_false" | "short_answer";

export interface ExamQuestion {
  id: string;
  position: number;
  text: string;
  type: QuestionType;
  points: number;
  explanation?: string | null;
  related_lesson_id?: string | null;
  answers: ExamAnswer[];
}

export interface ExamOut {
  id: string;
  course_id: string;
  title: string;
  time_limit_minutes: number;
  passing_score: number;
  max_attempts: number;
  questions: ExamQuestion[];
  shuffle_questions?: boolean;
  shuffle_answers?: boolean;
  show_correct_after?: boolean;
  allow_review?: boolean;
  issue_certificate?: boolean;
}

export interface StartAttemptResponse {
  attempt_id: string;
  started_at: string;
  expires_at: string;
  exam: ExamOut;
}

export interface AttemptResult {
  attempt_id: string;
  score: number;
  passed: boolean;
  total_questions: number;
  correct: number;
  time_spent_seconds: number;
}

export interface CertificateListItem {
  attempt_id: string;
  certificate_no: string;
  course_title: string;
  category: string;
  score: number | null;
  issued_at: string | null;
  download_url: string;
}

export interface PresignUploadResponse {
  asset_id: string;
  upload_url: string;
  object_key: string;
  bucket: string;
  expires_in_seconds: number;
}
